import { isSameDay } from "date-fns";
import { ignoredMessagesTypes } from "chat/constants";
import { TANGO_ACCOUNT_ID } from "chat/imports/constants";
import { getChatIdForLoadLandingsFromShouts } from "chat/imports/environment";
import {
  generateAsyncSelectors,
  typedDelegateSelectors,
} from "chat/imports/state";
import { getParsedExternalMessagePayload, uniq } from "chat/imports/utils";
import { ChatState, StoredMessage } from "chat/state/reducer";

const { data, meta } = generateAsyncSelectors<ChatState>();

export const localChatSelectors = {
  data,
  meta,
  getCurrentConversationId: (state: ChatState) =>
    data(state).currentConversationId,
  getMessageById: (
    state: ChatState,
    conversationId: null | string,
    messageId: number
  ) =>
    data(state).messages[conversationId || ""]?.find(
      (message) => message.id.id === messageId
    ),
  getConversationListScrollYPosition: (state: ChatState) =>
    data(state).conversationListScrollYPosition,
  getConversations: (state: ChatState) => {
    const { conversations } = data(state);

    return Object.values(conversations).sort((a, b) => {
      const aTs = a.last_message_ts || 0;
      const bTs = b.last_message_ts || 0;
      const aPinnedTs = a.pinnedTs || 0;
      const bPinnedTs = b.pinnedTs || 0;

      if (aPinnedTs || bPinnedTs) {
        return bPinnedTs - aPinnedTs;
      }

      if (aTs === bTs) {
        if (a.conversation_id === TANGO_ACCOUNT_ID) {
          return -1;
        }
        if (b.conversation_id === TANGO_ACCOUNT_ID) {
          return 1;
        }

        return a.conversation_id.localeCompare(b.conversation_id);
      }

      return bTs - aTs;
    });
  },
  getConversationAccountInfoByAccountId: (
    state: ChatState,
    accountId?: string
  ) => {
    const { conversations } = data(state);

    return Object.values(conversations).find(
      (conversation) => conversation?.account_info?.account_id === accountId
    );
  },
  getConversation: (state: ChatState, conversationId: string) =>
    data(state).conversations[conversationId],
  getIsFamilyConversation: (state: ChatState, conversationId: null | string) =>
    Boolean(
      data(state).conversations?.[conversationId || ""]?.group_info?.familyId
    ),
  getConversationLastSelfReadMessageTs: (
    state: ChatState,
    conversationId: string
  ) => data(state).conversations[conversationId].lastSelfReadMessageTs,
  getConversationLoadingFailed: (state: ChatState, conversationId?: string) => {
    if (conversationId) {
      return (
        data(state).conversations[conversationId]?.isLoadingFailed || false
      );
    }

    return false;
  },
  getMessagesByConversationId: (state: ChatState, conversationId: string) =>
    data(state).messages[conversationId],
  getConversationMessagesByDates: (
    state: ChatState,
    conversationId: string
  ) => {
    const preparedData = (data(state).messages[conversationId] || []).reduce(
      (
        acc: {
          accountIds: string[];
          dates: { date: number; messages: StoredMessage[] }[];
          lastMessageTs: number;
          latestMessageFrom: string | undefined;
          messagesIds: string[];
        },
        next
      ) => {
        if (ignoredMessagesTypes.includes(next.type)) {
          return acc;
        }
        acc.lastMessageTs = next.id.ts;
        if (acc.messagesIds.length === 0) {
          acc.latestMessageFrom = next.from;
        }
        acc.messagesIds.push(`${next.id.id}`);

        if (next.from) {
          acc.accountIds.push(next.from);
        }

        if (acc.dates.length === 0) {
          acc.dates.push({
            date: next.id.ts,
            messages: [next],
          });

          return acc;
        }

        const isSameDayTs = isSameDay(
          acc.dates[acc.dates.length - 1].date,
          next.id.ts
        );

        if (isSameDayTs) {
          acc.dates[acc.dates.length - 1].messages.push(next);
        } else {
          acc.dates.push({
            date: next.id.ts,
            messages: [next],
          });
        }

        return acc;
      },
      {
        dates: [],
        messagesIds: [],
        lastMessageTs: 0,
        accountIds: [],
        latestMessageFrom: undefined,
      }
    );
    preparedData.accountIds = uniq(preparedData.accountIds);

    return preparedData;
  },
  getConversationLatestMessage: (state: ChatState, conversationId: string) =>
    data(state).messages[conversationId]?.find(
      (message) => !ignoredMessagesTypes.includes(message.type)
    ),
  canLoadMore: (state: ChatState) => data(state).canLoadMore,
  getTotalUnreadCount: (state: ChatState) => data(state).totalUnreadCount,
  getUnreadCountByConversation: (state: ChatState, conversationId: string) =>
    data(state).conversations[conversationId]?.unread_message_count,
  getUnreadMessagesStatus: (state: ChatState) => state.data.readStatus.status,
  getIsUnreadRequestInProgress: (state: ChatState) =>
    state.data.readStatus.requestInProgress,
  getLastReadTs: (state: ChatState) => state.data.readStatus.lastReadMessageTs,
  getLastReadConversations: (state: ChatState) =>
    state.data.readStatus.readMessageData,
  getLastReadConversationTs: (state: ChatState, conversationId: string) =>
    state.data.readStatus.readMessageData[conversationId],
  getSelfReadStatus: (state: ChatState) => state.data.selfReadStatus.status,
  getIsSelfReadRequestInProgress: (state: ChatState) =>
    state.data.selfReadStatus.requestInProgress,
  getLastSelfReadTs: (state: ChatState) =>
    state.data.selfReadStatus.lastSelfReadMessageTs,
  getLastServerChatTimestamp: (state: ChatState) =>
    state.data.chatTimestamps.lastServerChatTimestamp,
  getLastLocalReadConversationTs: (state: ChatState, conversationId: string) =>
    state.data.localReadStatus[conversationId],
  getLastReadSelfReaction: (state: ChatState, conversationId: string) =>
    state.data.readSelfReactionStatus.readReactionData[conversationId],
  getLastReadSelfReactionTs: (state: ChatState) =>
    state.data.readSelfReactionStatus.lastReadReactionTs,
  getLastReactionTimeStamp: (state: ChatState) =>
    state.data.messageReactions.lastReactionTs,
  getUnreadReactionStatus: (state: ChatState) => state.data.readReactionStatus,
  getSelfReactionStatus: (state: ChatState) =>
    state.data.readSelfReactionStatus.status,
  getSelfReactionTs: (state: ChatState) =>
    state.data.readSelfReactionStatus.lastReadReactionTs,
  getSelfReaction: (state: ChatState) =>
    state.data.readSelfReactionStatus.readReactionData,
  getReactionStatus: (state: ChatState) => state.data.readReactionStatus,
  getChatEventsStatus: (state: ChatState) => state.data.chatEvents.status,
  getLastChatEventTs: (state: ChatState) => state.data.chatEvents.lastEventTs,
  getChatEvents: (state: ChatState) => state.data.chatEvents.events,
  getPinnedConversationsCount: (state: ChatState) =>
    data(state).pinnedConversationsCount,
  isConversationPinned: (state: ChatState, conversationId: string) =>
    data(state).conversations[conversationId]?.isPinned,
  getEditingMessageInProgress: (state: ChatState) =>
    data(state).editingMessageInProgress,
  getIsNeededToFetchBatchOfMessages: (state: ChatState) =>
    data(state).shouldUpdateBatchOfMessages,
  getDeletedChatUserId: (state: ChatState) => data(state).deletedChatUserId,
  getTranscription: (
    state: ChatState,
    conversationId: null | string,
    messageId: number
  ) =>
    state.data.messages[conversationId || ""].find(
      (item) => item.id.id === messageId
    )?.transcription?.data,
  getTouchedReactionInfo: (state: ChatState) => data(state).touchReactions,
  getMessageReactions: (
    state: ChatState,
    conversationId: null | string,
    messageId: number
  ) =>
    data(state).messageReactions?.conversationReactions[conversationId || ""]?.[
      messageId
    ],
  getIsMessageHasSingleReaction: (
    state: ChatState,
    conversationId: null | string,
    messageId: number
  ) =>
    (
      data(state).messageReactions.conversationReactions[
        conversationId || ""
      ]?.[messageId]?.messageReactions ?? []
    ).length === 1 &&
    data(state).messageReactions.conversationReactions[conversationId || ""]?.[
      messageId
    ]?.messageReactions[0].reactionCount === 1,
  getIsMessageHasReactions: (
    state: ChatState,
    conversationId: null | string,
    messageId: number | undefined
  ) =>
    Boolean(
      data(state).messageReactions.conversationReactions[
        conversationId || ""
      ]?.[messageId || ""]?.messageReactions.length
    ),
  getMyMessageReaction: (
    state: ChatState,
    conversationId: null | string,
    messageId: number | undefined
  ) =>
    data(state).messageReactions.conversationReactions?.[
      conversationId || ""
    ]?.[messageId || ""]?.myReactionId,
  getLpIds: (state: ChatState) => {
    const messages = data(state).messages[getChatIdForLoadLandingsFromShouts()];
    if (!messages) {
      return null;
    }

    return messages.reduce((acc: string[], message) => {
      if (!message.sdk_message_payload?.payload) {
        return acc;
      }

      const parsedPayload = getParsedExternalMessagePayload(
        message.sdk_message_payload.payload
      );

      if (!parsedPayload) {
        return acc;
      }

      const match = parsedPayload.actionInfo?.[0]?.actionUrl.match(
        /\/deeplink\/q\?additional_action=(.*?)(&|$)/
      );

      if (match && match[1] && !acc.includes(match[1])) {
        acc.push(match[1]);
      }

      return acc;
    }, []);
  },
  getPlayingMedia: (state: ChatState) => data(state).playingMedia,
  getIsLightboxImageOpen: (state: ChatState) => data(state).isLightboxImageOpen,
};

export default typedDelegateSelectors(localChatSelectors, "chat");
